import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Rating,
  Switch,
  FormControlLabel,
  Autocomplete,
  Divider,
  Card,
  CardMedia
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  Hotel as HotelIcon
} from '@mui/icons-material';
import { hotelService, authService } from '../../services';

// Hotel categories
const HOTEL_CATEGORIES = [
  'budget',
  'standard',
  'premium',
  'luxury',
  'heritage',
  'boutique'
];

// Common amenities for autocomplete
const COMMON_AMENITIES = [
  'Free WiFi',
  'Air Conditioning',
  'Room Service',
  'Restaurant',
  'Parking',
  'Swimming Pool',
  'Gym',
  'Spa',
  'Laundry Service',
  'Conference Room',
  'Bar',
  'Airport Shuttle',
  'Breakfast Included',
  '24-Hour Front Desk',
  'Elevator',
  'Garden',
  'Terrace',
  'Non-smoking Rooms',
  'Family Rooms',
  'Luggage Storage'
];

// Common landmarks for distance calculation
const COMMON_LANDMARKS = [
  'Ram Mandir',
  'Hanuman Garhi',
  'Kanak Bhawan',
  'Saryu River',
  'Ayodhya Railway Station',
  'Bus Stand',
  'City Center'
];

const HotelManagement = () => {
  // State for hotels data
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [currentHotel, setCurrentHotel] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [category, setCategory] = useState('standard');
  const [starRating, setStarRating] = useState(3);
  const [images, setImages] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [amenities, setAmenities] = useState([]);
  const [rating, setRating] = useState(0);
  const [featured, setFeatured] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [distanceFromLandmarks, setDistanceFromLandmarks] = useState({});
  
  // Check if user has permission
  const isAdmin = authService.hasRole('admin');
  const isManager = authService.hasRole('manager');
  const hasAccess = isAdmin || isManager;
  
  // Fetch hotels on component mount
  useEffect(() => {
    if (!hasAccess) {
      setError('You do not have permission to access this page.');
      return;
    }
    
    fetchHotels();
  }, [hasAccess]);
  
  // Fetch all hotels
  const fetchHotels = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const hotelsData = await hotelService.fetchAllHotels();
      setHotels(hotelsData);
    } catch (err) {
      console.error('Error fetching hotels:', err);
      setError('Failed to load hotels. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Open dialog for creating a new hotel
  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setName('');
    setDescription('');
    setLocation('');
    setAddress('');
    setLatitude('');
    setLongitude('');
    setCategory('standard');
    setStarRating(3);
    setImages([]);
    setNewImageUrl('');
    setPriceRange('');
    setAmenities([]);
    setRating(0);
    setFeatured(false);
    setPhoneNumber('');
    setEmail('');
    setWebsite('');
    setDistanceFromLandmarks({});
    setCurrentHotel(null);
    setOpenDialog(true);
  };
  
  // Open dialog for editing an existing hotel
  const handleOpenEditDialog = (hotel) => {
    setDialogMode('edit');
    setName(hotel.name);
    setDescription(hotel.description);
    setLocation(hotel.location);
    setAddress(hotel.address);
    setLatitude(hotel.latitude?.toString() || '');
    setLongitude(hotel.longitude?.toString() || '');
    setCategory(hotel.category);
    setStarRating(hotel.starRating);
    setImages(hotel.images || []);
    setNewImageUrl('');
    setPriceRange(hotel.priceRange);
    setAmenities(hotel.amenities || []);
    setRating(hotel.rating);
    setFeatured(hotel.featured);
    setPhoneNumber(hotel.phoneNumber || '');
    setEmail(hotel.email || '');
    setWebsite(hotel.website || '');
    setDistanceFromLandmarks(hotel.distanceFromLandmarks || {});
    setCurrentHotel(hotel);
    setOpenDialog(true);
  };
  
  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Add image URL to images array
  const handleAddImage = () => {
    if (newImageUrl && !images.includes(newImageUrl)) {
      setImages([...images, newImageUrl]);
      setNewImageUrl('');
    }
  };
  
  // Remove image from images array
  const handleRemoveImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };
  
  // Update distance from landmark
  const handleDistanceChange = (landmark, value) => {
    setDistanceFromLandmarks({
      ...distanceFromLandmarks,
      [landmark]: parseFloat(value) || 0
    });
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!name || !description || !location || !address || !priceRange) {
      setError('Please fill in all required fields.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const hotelData = {
        name,
        description,
        location,
        address,
        latitude: latitude ? parseFloat(latitude) : undefined,
        longitude: longitude ? parseFloat(longitude) : undefined,
        category,
        starRating,
        images,
        priceRange,
        amenities,
        rating: parseFloat(rating),
        featured,
        phoneNumber: phoneNumber || undefined,
        email: email || undefined,
        website: website || undefined,
        distanceFromLandmarks
      };
      
      if (dialogMode === 'create') {
        await hotelService.createHotel(hotelData);
      } else {
        await hotelService.updateHotel(currentHotel.id, hotelData);
      }
      
      // Refresh hotels list
      await fetchHotels();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving hotel:', err);
      setError(err.response?.data?.message || 'Failed to save hotel. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle hotel deletion
  const handleDeleteHotel = async (hotelId) => {
    if (!window.confirm('Are you sure you want to delete this hotel? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await hotelService.deleteHotel(hotelId);
      // Refresh hotels list
      await fetchHotels();
    } catch (err) {
      console.error('Error deleting hotel:', err);
      setError(err.response?.data?.message || 'Failed to delete hotel. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Get category label
  const getCategoryLabel = (categoryValue) => {
    return categoryValue.charAt(0).toUpperCase() + categoryValue.slice(1);
  };
  
  // If not admin or manager, show access denied
  if (!hasAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. You must be an admin or manager to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Hotel Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          disabled={loading}
        >
          Add New Hotel
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Price Range</TableCell>
                <TableCell>Featured</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hotels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No hotels found.
                  </TableCell>
                </TableRow>
              ) : (
                hotels.map((hotel) => (
                  <TableRow key={hotel.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {hotel.images && hotel.images.length > 0 ? (
                          <Box
                            component="img"
                            src={hotel.images[0]}
                            alt={hotel.name}
                            sx={{
                              width: 50,
                              height: 50,
                              objectFit: 'cover',
                              borderRadius: 1,
                              mr: 2
                            }}
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                            }}
                          />
                        ) : (
                          <HotelIcon sx={{ fontSize: 40, mr: 2 }} />
                        )}
                        <Typography variant="body1">{hotel.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{hotel.location}</TableCell>
                    <TableCell>
                      <Chip
                        label={getCategoryLabel(hotel.category)}
                        color={hotel.category === 'luxury' || hotel.category === 'premium' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Rating value={hotel.rating} readOnly precision={0.5} size="small" />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          ({hotel.rating})
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{hotel.priceRange}</TableCell>
                    <TableCell>
                      {hotel.featured ? (
                        <Chip label="Featured" color="success" size="small" />
                      ) : (
                        <Chip label="Not Featured" variant="outlined" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(hotel)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteHotel(hotel.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Create/Edit Hotel Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Hotel' : 'Edit Hotel'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="name"
                label="Hotel Name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="location"
                label="Location"
                name="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="address"
                label="Full Address"
                name="address"
                multiline
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="latitude"
                label="Latitude"
                name="latitude"
                type="number"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                disabled={loading}
                InputProps={{
                  inputProps: { step: 'any' }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="longitude"
                label="Longitude"
                name="longitude"
                type="number"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                disabled={loading}
                InputProps={{
                  inputProps: { step: 'any' }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="description"
                label="Description"
                name="description"
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Classification & Pricing
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  value={category}
                  label="Category"
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={loading}
                >
                  {HOTEL_CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {getCategoryLabel(cat)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography component="legend">Star Rating</Typography>
                <Rating
                  name="starRating"
                  value={starRating}
                  onChange={(event, newValue) => {
                    setStarRating(newValue);
                  }}
                  disabled={loading}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="priceRange"
                label="Price Range (e.g., ₹1000 - ₹5000)"
                name="priceRange"
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={featured}
                    onChange={(e) => setFeatured(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Featured Hotel"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Amenities
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="amenities"
                options={COMMON_AMENITIES}
                freeSolo
                value={amenities}
                onChange={(event, newValue) => {
                  setAmenities(newValue);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      key={index}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Amenities"
                    placeholder="Add amenities"
                    helperText="Type and press Enter to add custom amenities"
                  />
                )}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Contact Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                id="phoneNumber"
                label="Phone Number"
                name="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                id="email"
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                id="website"
                label="Website"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: <WebsiteIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Distance from Landmarks (km)
              </Typography>
            </Grid>
            
            {COMMON_LANDMARKS.map((landmark) => (
              <Grid item xs={12} md={4} key={landmark}>
                <TextField
                  fullWidth
                  id={`landmark-${landmark}`}
                  label={landmark}
                  type="number"
                  value={distanceFromLandmarks[landmark] || ''}
                  onChange={(e) => handleDistanceChange(landmark, e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                    inputProps: { min: 0, step: 0.1 }
                  }}
                />
              </Grid>
            ))}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Images
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  fullWidth
                  id="newImageUrl"
                  label="Image URL"
                  name="newImageUrl"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  disabled={loading}
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddImage}
                  disabled={loading || !newImageUrl}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
              
              <Grid container spacing={2}>
                {images.map((imageUrl, index) => (
                  <Grid item xs={6} md={3} key={index}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="140"
                        image={imageUrl}
                        alt={`Hotel image ${index + 1}`}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/140x140?text=Invalid+Image+URL';
                        }}
                      />
                      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" noWrap sx={{ maxWidth: '70%' }}>
                          Image {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveImage(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
                {images.length === 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      No images added yet. Add image URLs to showcase the hotel.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !name || !description || !location || !address || !priceRange}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HotelManagement;
